import { RolesWithProduct } from '@customTypes/api/Commons';
import useFeatureFlags from '@hooks/useFeatureFlags';
import AuthGate from '@pages/AuthGate/screens';
import { useAuthorizationContext } from '@src/context/AuthorizationContext';
import { lazyLoad } from '@src/utils/loadable';
import { isDateInSetTargetPeriod } from '@src/utils/utils';
import React from 'react';
import { Switch } from 'react-router-dom';

import { PrivateLayout } from '../layouts';

import pathnames from './pathnames';
import Route from './RouteWithRedirect';

const AgencyNewsUpdates = lazyLoad(
  () => import('@pages/AgencyNews/Updates'),
  module => module.AgencyNewsUpdates,
);

const Notification = lazyLoad(
  () => import('@pages/Notification'),
  module => module.NotificationPage,
);

const AgencyNewsPromoMaterial = lazyLoad(
  () => import('@pages/AgencyNews/PromoMaterial'),
  module => module.AgencyNewsPromoMaterial,
);
const AgencyNewsLoyaltyProgram = lazyLoad(
  () => import('@pages/AgencyNews/LoyaltyProgram'),
  module => module.AgencyNewsLoyaltyProgram,
);
const AgencyNewsTrainingMaterial = lazyLoad(
  () => import('@pages/AgencyNews/TrainingMaterial'),
  module => module.AgencyNewsTrainingMaterial,
);
const Maintain = lazyLoad(
  () => import('@pages/PlanActivity/Maintain'),
  module => module.Maintain,
);
const MaintainNonRegist = lazyLoad(
  () => import('@pages/PlanActivity/MaintainNonRegist'),
  module => module.MaintainNonRegist,
);
const AreSheduleDetail = lazyLoad(
  () => import('@pages/PlanActivity/Maintain/ARESchedule/ScheduleDetail'),
  module => module.ScheduleDetail,
);

const SheduleDetail = lazyLoad(
  () => import('@pages/PlanActivity/MaintainNonRegist/Schedule/ScheduleDetail'),
  module => module.ScheduleDetail,
);

const Recruit = lazyLoad(
  () => import('@pages/PlanActivity/Recruit'),
  module => module.Recruit,
);

const SPVDashboard = lazyLoad(
  () => import('@pages/PerformanceDashboard/SPVDashboard'),
  module => module.SPVDashboardPage,
);
const BMPerformance = lazyLoad(
  () => import('@pages/PerformanceDashboard/BMPerformance'),
  module => module.BMPerformancePage,
);
const AreaPerformance = lazyLoad(
  () => import('@pages/PerformanceDashboard/AreaPerformance'),
  module => module.AreaPerformancePage,
);
const TemporaryScreen = lazyLoad(
  () => import('@pages/TemporaryScreen'),
  module => module.TemporaryScreen,
);

const GoalSettings = lazyLoad(
  () => import('@pages/PerformanceDashboard/GoalSettings'),
  module => module.GoalSettingsPage,
);

const AgentPerformanceThisMonth = lazyLoad(
  () => import('@pages/PerformanceDashboard/AgentPerformance/ThisMonth'),
  module => module.AgentPerformanceThisMonthPage,
);

const AgentPerformanceHistorical = lazyLoad(
  () => import('@pages/PerformanceDashboard/AgentPerformance/Historical'),
  module => module.AgentPerformanceHistoricalPage,
);

const AREPerformance = lazyLoad(
  () => import('@pages/PerformanceDashboard/AREPerformance'),
  module => module.AREPerformancePage,
);

const KpiIncentive = lazyLoad(
  () => import('@pages/PerformanceDashboard/KpiIncentive'),
  module => module.KpiIncentive,
);

const NotFoundPage = lazyLoad(
  () => import('@components/NotFoundPage'),
  module => module.NotFoundPage,
);

const AgentManagement = lazyLoad(
  () => import('@pages/TeamManagement/AgentManagement'),
  module => module.AgentManagement,
);
const AgentManagementDetail = lazyLoad(
  () => import('@pages/TeamManagement/AgentManagement/Detail'),
  module => module.AgentManagementDetail,
);

const AgentManagementV2 = lazyLoad(
  () => import('@pages/TeamManagement/AgentManagementV2'),
  module => module.AgentManagement,
);
const AgentManagementDetailV2 = lazyLoad(
  () => import('@pages/TeamManagement/AgentManagementV2/Detail'),
  module => module.AgentManagementDetail,
);

const Hierarchy = lazyLoad(
  () => import('@pages/TeamManagement/Hierarchy'),
  module => module.Hierarchy,
);

const AddNewAssignment = lazyLoad(
  () => import('@pages/PlanActivity/Maintain/AddNewAssignment'),
  module => module.AddNewAssignment,
);

const AddNewRecruitAssignment = lazyLoad(
  () => import('@pages/PlanActivity/Recruit/AddNewAssignment'),
  module => module.AddNewRecruitAssignment,
);

const AddNewMaintainNonRegistAssignment = lazyLoad(
  () => import('@pages/PlanActivity/MaintainNonRegist/AddNewAssignment'),
  module => module.AddNewAssignment,
);

const WeeklyMeeting = lazyLoad(
  () => import('@pages/PlanActivity/WeeklyMeeting'),
  module => module.WeeklyMeeting,
);
const AddNewWeeklyMeetingReport = lazyLoad(
  () => import('@pages/PlanActivity/WeeklyMeeting/AddNewReport'),
  module => module.AddNewWeeklyMeetingReport,
);

const JoinVisit = lazyLoad(
  () => import('@pages/PlanActivity/JoinVisit'),
  module => module.JoinVisit,
);

const OneOnOne = lazyLoad(
  () => import('@pages/PlanActivity/OneOnOne'),
  module => module.OneOnOne,
);
const AddNewOneOnOneReport = lazyLoad(
  () => import('@pages/PlanActivity/OneOnOne/AddNewReport'),
  module => module.AddNewReport,
);
const AddNewOneOnOneReportV2 = lazyLoad(
  () => import('@pages/PlanActivity/OneOnOne/AddNewReportV2'),
  module => module.AddNewReport,
);

const BranchRoutineChecklist = lazyLoad(
  () => import('@pages/PlanActivity/BranchRoutineChecklist'),
  module => module.BranchRoutineChecklist,
);

const AddNewJoinVisitReport = lazyLoad(
  () => import('@pages/PlanActivity/JoinVisit/AddNewReport'),
  module => module.AddNewReport,
);

const AddNewJoinVisitReportV2 = lazyLoad(
  () => import('@pages/PlanActivity/JoinVisit/AddNewReportV2'),
  module => module.AddNewReport,
);

const MaintainReport = lazyLoad(
  () => import('@pages/Reports/MaintainReport'),
  module => module.MaintainReport,
);

const MaintainReportV2 = lazyLoad(
  () => import('@pages/Reports/MaintainReportV2'),
  module => module.MaintainReport,
);

const MaintainReportList = lazyLoad(
  () => import('@pages/Reports/MaintainReport/List'),
  module => module.MaintainReportList,
);

const RecruitReport = lazyLoad(
  () => import('@pages/Reports/Recruit'),
  module => module.RecruitReport,
);

const LeadsTracker = lazyLoad(
  () => import('@pages/LeadsTracker/List'),
  module => module.LeadsTrackerList,
);
const LeadsTrackerDetail = lazyLoad(
  () => import('@pages/LeadsTracker/Detail'),
  module => module.LeadsTrackerDetail,
);
const LeadsTrackerDetailV2 = lazyLoad(
  () => import('@pages/LeadsTracker/DetailV2'),
  module => module.LeadsTrackerDetail,
);

const SettingAgentExpiry = lazyLoad(
  () => import('@pages/SettingAgentExpiry'),
  module => module.SettingAgentExpiry,
);

const UserGuide = lazyLoad(
  () => import('@pages/UserGuide'),
  module => module.UserGuide,
);

const UnauthorizedPage = lazyLoad(
  () => import('@pages/Unauthorized'),
  module => module.UnauthorizedPage,
);

export const AppRouter = () => {
  const { features, routes } = useFeatureFlags();

  const { isAllowed, rolesProduct } = useAuthorizationContext();

  const isPBF = rolesProduct === RolesWithProduct['SPV.PBF'];

  const renderDynamicHome = () => {
    const homeRoutes: { [key: string]: React.ReactNode } = {
      [RolesWithProduct['SPV.4W']]: (
        <Route
          key="SpvDashboard"
          exact
          path="/"
          enabled={isAllowed('spv-dashboard')}
          render={() => <SPVDashboard />}
        />
      ),
      [RolesWithProduct['SPV.2W']]: isAllowed('spv-dashboard') && (
        <Route
          key="SpvDashboard"
          exact
          path="/"
          render={() => <SPVDashboard />}
        />
      ),
      [RolesWithProduct['SPV.PBF']]: isAllowed('spv-dashboard') &&
        features.activityDashboard() && (
          <Route
            key="SpvDashboard"
            exact
            path="/"
            render={() => <SPVDashboard />}
          />
        ),
      [RolesWithProduct['BM']]: isAllowed('branch-dashboard') && (
        <Route
          key="BmDashboard"
          exact
          path="/"
          render={() => <BMPerformance />}
        />
      ),
      [RolesWithProduct['ABPM']]: isAllowed('area-dashboard') && (
        <Route
          path="/"
          render={() => <AreaPerformance />}
          exact
          key="AreaPerformanceABPM"
        />
      ),
      [RolesWithProduct['AM']]: isAllowed('area-dashboard') && (
        <Route
          key="AreaPerformanceAM"
          exact
          path="/"
          render={() => <AreaPerformance />}
        />
      ),
    };
    return (
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      homeRoutes[rolesProduct] || (
        <Route
          key="temporary-screen"
          exact
          path="/"
          render={() => <TemporaryScreen />}
        />
      )
    );
  };

  return (
    <Switch>
      <Route
        key="login"
        exact
        path={pathnames.login()}
        render={() => <AuthGate />}
      />
      <PrivateLayout>
        <Switch>
          <Route
            key="notification"
            exact
            path={pathnames.notification()}
            enabled={features.showNotification()}
            render={() => <Notification />}
          />
          <Route
            key="bm-performance"
            exact
            path={pathnames.branchPerformance()}
            enabled={isAllowed('branch-performance')}
            render={() => <BMPerformance />}
          />
          <Route
            key="SpvPerformance"
            exact
            path={pathnames.spvPerformance()}
            enabled={isAllowed('spv-performance')}
            render={() => <SPVDashboard />}
          />
          <Route
            key="BmPerformance"
            exact
            path={pathnames.branchPerformance()}
            enabled={isAllowed('branch-performance')}
            render={() => <BMPerformance />}
          />
          <Route
            key="agency-news-updates"
            exact
            path={pathnames.agencyNewsUpdates()}
            enabled={isAllowed('agency-news.updates') && !isPBF}
            render={() => <AgencyNewsUpdates />}
          />
          <Route
            key="agency-news-promo-material"
            exact
            path={pathnames.agencyNewsPromoMaterial()}
            enabled={isAllowed('agency-news.promo-material') && !isPBF}
            render={() => <AgencyNewsPromoMaterial />}
          />
          <Route
            key="agency-news-loyalty-program"
            exact
            path={pathnames.agencyNewsLoyaltyProgram()}
            enabled={isAllowed('agency-news.loyalty-program') && !isPBF}
            render={() => <AgencyNewsLoyaltyProgram />}
          />
          <Route
            key="agency-news-training-material"
            exact
            path={pathnames.agencyNewsTrainingMaterial()}
            enabled={isAllowed('agency-news.training-material') && !isPBF}
            render={() => <AgencyNewsTrainingMaterial />}
          />

          <Route
            key="agent-performance"
            exact
            path={pathnames.agentPerformance()}
            enabled={isAllowed('agent-performance.this-month') && !isPBF}
            render={() => <AgentPerformanceThisMonth />}
          />
          <Route
            key="agent-performance-historical"
            exact
            path={pathnames.historicalAgentPerformance(':agentMarketingId')}
            enabled={isAllowed('agent-performance.historical') && !isPBF}
            render={() => <AgentPerformanceHistorical />}
          />
          <Route
            key="maintain"
            exact
            path={pathnames.maintainActivity()}
            enabled={
              isAllowed('maintain.assignment') ||
              isAllowed('maintain.are-schedule')
            }
            render={() => <Maintain />}
          />
          <Route
            key="are-schedule-detail"
            exact
            path={pathnames.maintainActivityScheduleDetail(':id')}
            render={() => <AreSheduleDetail />}
          />
          <Route
            key="maintain-non-regist"
            exact
            path={pathnames.maintainNonRegistActivity()}
            enabled={
              isAllowed('maintain-non-regist.assignment') ||
              isAllowed('maintain-non-regist.are-schedule') ||
              !isPBF
            }
            render={() => <MaintainNonRegist />}
          />
          <Route
            key="maintain-non-regist-detail"
            exact
            path={pathnames.maintainNonRegistActivityScheduleDetail(':id')}
            render={() => <SheduleDetail />}
          />
          <Route
            key="add-new-maintain-non-regist-assignment"
            exact
            path={pathnames.addNewMaintainNonRegistAssignment()}
            enabled={
              isAllowed('maintain-non-regist.assignment', 'create') || !isPBF
            }
            render={() => <AddNewMaintainNonRegistAssignment />}
          />
          <Route
            key="are-performance"
            exact
            path={pathnames.arePerformance()}
            enabled={isAllowed('are-performance') && !isPBF}
            render={() => <AREPerformance />}
          />
          <Route
            key="kpi-incentive"
            exact
            path={pathnames.kpiIncentive()}
            enabled={
              isAllowed('kpi-incentive') &&
              (!isPBF || routes.kpiIncentiveForPBF())
            }
            render={() => <KpiIncentive />}
          />
          {renderDynamicHome()}
          <Route
            key="set-target"
            exact
            enabled={
              isAllowed('goal-settings') &&
              isDateInSetTargetPeriod(new Date(Date.now())) &&
              !isPBF
            }
            path={pathnames.setTarget()}
            render={() => <GoalSettings />}
          />
          <Route
            key="agent-management"
            exact
            path={pathnames.agentManagement()}
            enabled={
              (isAllowed('agent-management') ||
                isAllowed('agent-management.blacklist')) &&
              (!isPBF || routes.agentManagementForPBF())
            }
            render={() =>
              routes.agentManagementV2() ? (
                <AgentManagementV2 />
              ) : (
                <AgentManagement />
              )
            }
          />
          <Route
            key="agent-management-detail"
            exact
            path={pathnames.agentManagementDetail(':baId')}
            enabled={
              isAllowed('agent-management') &&
              (!isPBF || routes.agentManagementForPBF())
            }
            render={() =>
              routes.agentManagementV2() ? (
                <AgentManagementDetailV2 />
              ) : (
                <AgentManagementDetail />
              )
            }
          />
          <Route
            key="agent-management-blacklist-detail"
            exact
            path={pathnames.agentManagementBlacklistDetail(':baId')}
            enabled={
              isAllowed('agent-management.blacklist') &&
              (!isPBF || routes.agentManagementForPBF())
            }
            render={() => <AgentManagementDetail />}
          />
          <Route
            key="hierarchy"
            exact
            path={pathnames.teamHierarchy()}
            enabled={
              isAllowed('hierarchy') && (!isPBF || routes.hierarchyForPBF())
            }
            render={() => <Hierarchy />}
          />
          <Route
            key="recruit"
            exact
            path={pathnames.recruitActivity()}
            enabled={
              isAllowed('recruitment.recruitment-area') ||
              isAllowed('recruitment.recruitment-list')
            }
            render={() => <Recruit />}
          />
          <Route
            key="add-new-assignment"
            exact
            path={pathnames.addNewMaintainAssignment()}
            enabled={isAllowed('maintain.assignment', 'create')}
            render={() => <AddNewAssignment />}
          />
          <Route
            key="add-new-recruit-assignment"
            exact
            path={pathnames.addNewRecruitAssignment()}
            enabled={isAllowed(
              'recruitment.recruitment-area.assignment',
              'create',
            )}
            render={() => <AddNewRecruitAssignment />}
          />
          <Route
            key="weekly-meeting"
            exact
            path={pathnames.weeklyMeeting()}
            render={() => <WeeklyMeeting />}
            enabled={isAllowed('weekly-meeting') && !isPBF}
          />
          <Route
            key="add-new-weekly-meeting-report"
            exact
            path={pathnames.addNewWeeklyMeetingReport()}
            render={() => <AddNewWeeklyMeetingReport />}
            enabled={isAllowed('weekly-meeting', 'create') && !isPBF}
          />
          <Route
            key="join-visit"
            exact
            path={pathnames.joinVisit()}
            render={() => <JoinVisit />}
            enabled={
              isAllowed('join-visit.this-month') ||
              isAllowed('join-visit.historical')
            }
          />
          <Route
            key="one-on-one"
            exact
            path={pathnames.oneOnOne()}
            render={() => <OneOnOne />}
            enabled={
              // SPV
              ((isAllowed('1-on-1.sales') ||
                isAllowed('1-on-1.maintain') ||
                isAllowed('1-on-1.recruit')) &&
                (!isPBF || routes['1on1ForPBF']())) ||
              // BM
              isAllowed('1-on-1-bm.sales') ||
              isAllowed('1-on-1-bm.maintain') ||
              isAllowed('1-on-1-bm.recruit')
            }
          />
          <Route
            key="add-new-one-on-one-report"
            exact
            path={pathnames.addNewOneOnOneReport()}
            render={() =>
              features.dynamicSpv1On1Suggestions() ? (
                <AddNewOneOnOneReportV2 />
              ) : (
                <AddNewOneOnOneReport />
              )
            }
            enabled={
              isAllowed('1-on-1', 'create') || isAllowed('1-on-1-bm', 'create')
            }
          />
          <Route
            key="branch-routine-checklist"
            exact
            path={pathnames.branchRoutineChecklist()}
            render={() => <BranchRoutineChecklist />}
            enabled={
              isAllowed('branch-routine-checklist') &&
              features.branchRoutineChecklist()
            }
          />
          <Route
            key="add-new-report join-visit"
            exact
            path={pathnames.addNewJoinVisitReport()}
            render={() =>
              features.createJoinVisitReportV2() ? (
                <AddNewJoinVisitReportV2 />
              ) : (
                <AddNewJoinVisitReport />
              )
            }
            enabled={isAllowed('join-visit', 'create')}
          />

          <Route
            key="maintain-report"
            exact
            enabled={isAllowed('report.maintain')}
            path={pathnames.maintainReport()}
            render={() =>
              features.maintainReportV2() ? (
                <MaintainReportV2 />
              ) : (
                <MaintainReport />
              )
            }
          />

          <Route
            key="maintain-report-list"
            exact
            enabled={isAllowed('report.maintain.list')}
            path={`${pathnames.maintainReportList(':agent_marketing_id')}`}
            render={() => <MaintainReportList />}
          />

          <Route
            key="recruit-report"
            exact
            enabled={isAllowed('report.recruit')}
            path={pathnames.recruitReport()}
            render={() => <RecruitReport />}
          />

          <Route
            key="leads-tracker"
            exact
            enabled={isAllowed('leads-tracker') && !isPBF}
            path={pathnames.leadsTracker()}
            render={() => <LeadsTracker />}
          />
          <Route
            key="leads-tracker-detail"
            exact
            enabled={isAllowed('leads-tracker') && !isPBF}
            path={pathnames.leadsTrackerDetail(
              ':agentId',
              ':applicationGroupId',
              ':applicationId',
            )}
            render={() =>
              features.leadsTrackerDetailV2() ? (
                <LeadsTrackerDetailV2 />
              ) : (
                <LeadsTrackerDetail />
              )
            }
          />

          <Route
            key="setting-agent-expiry"
            exact
            enabled={isAllowed('setting-agent-expiry')}
            path={pathnames.settingAgentExpiry()}
            render={() => <SettingAgentExpiry />}
          />
          <Route
            key="user-guide"
            exact
            path={pathnames.userGuide()}
            render={() => <UserGuide />}
          />
          <Route
            key="UnauthorizedPage"
            path={pathnames.unauthorized()}
            component={UnauthorizedPage}
          />
          <Route key="NotFoundPage" path="*" component={NotFoundPage} />
        </Switch>
      </PrivateLayout>
    </Switch>
  );
};
